// navigation-completion Stage E — frontend API client for the Inspector →
// Navigation (bookmarks + hyperlinks) correction workflow.
//
// A SEPARATE module from v2Client.ts (which this phase does not edit); it reuses
// v2Client's exported `apiFetch` so auth headers, base URL and error handling
// are shared, never re-implemented. The wire types mirror the server envelope in
// src/services/navigation/draftService.ts EXACTLY — the UI reads server facts
// (handles, frozen targets, decisions, qualification) and never re-derives them.
//
// Every mutation is an optimistic compare-and-swap: the caller echoes the
// revision, artifact hash and policy version it saw, plus a non-empty reason. A
// 409 means another writer moved the draft — the reviewer must refresh, never
// silently retry. The server is authoritative for every authorization and
// target-resolution decision; this module validates only shape.

import { apiFetch } from '@/api/v2Client';
import type { NavigationOperation } from '@/api/navigationPolicy';

export type { NavigationOperation };

// ---------------------------------------------------------------------------
// Targets / geometry (mirror pdfEditor.ts LinkTarget / Rectangle / LinkStyle)
// ---------------------------------------------------------------------------

export interface Rectangle {
  readonly x0: number;
  readonly y0: number;
  readonly x1: number;
  readonly y1: number;
}

/** A selected /XYZ landing point in PDF user space (origin bottom-left). Mirrors
 *  the server's DestinationPoint — the ONE canonical destination-geometry shape.
 *  Omitted ⇒ page-top (`/XYZ null null null`, inherit the current view). */
export interface DestinationPoint {
  readonly x: number;
  readonly y: number;
}

export type LinkTarget =
  | { readonly kind: 'localPage'; readonly pageIndex: number; readonly at?: DestinationPoint }
  | { readonly kind: 'named'; readonly name: string }
  | {
      readonly kind: 'remoteGoToR';
      readonly path: string;
      readonly dest?:
        | { readonly kind: 'page'; readonly pageIndex: number; readonly at?: DestinationPoint }
        | { readonly kind: 'named'; readonly name: string };
    };

export interface LinkStyle {
  readonly color?: readonly [number, number, number];
  readonly borderWidth?: number;
}

// ---------------------------------------------------------------------------
// Remote (cross-document) WIRE target — the ONLY way the client names a
// current/prior document destination.
//
// A reviewer never submits a raw `remoteGoToR { path }`: an arbitrary path/hash
// is a target the server cannot verify. Instead the picker chooses a target from
// the authoritative inventory (navigationTargets.ts) and submits its OPAQUE id
// plus the exact `contextVersion` and `artifactSha256` it saw. The server
// re-builds the inventory, matches the id, re-asserts the hash and the context
// version (a 409 means refresh — never an automatic retry), and only then
// resolves the frozen GoToR dependency. This is the `remoteTarget` wire kind the
// navigation-draft editor accepts (see src/services/navigation/targetService.ts
// RemoteTargetRequest); it is DISTINCT from the resolved `remoteGoToR` runtime
// LinkTarget, which the server produces and the client only ever reads.
// ---------------------------------------------------------------------------

export interface RemoteWireTarget {
  readonly kind: 'remoteTarget';
  readonly targetId: string;
  readonly expectedContextVersion: string;
  readonly expectedTargetSha256: string;
  readonly destination:
    | { readonly kind: 'page'; readonly pageIndex: number; readonly at?: DestinationPoint }
    | { readonly kind: 'named'; readonly name: string };
}

/** What an edit / manual disposition may SUBMIT as a target: an in-document
 *  destination, or a server-authorized remote (cross-document) selection. The
 *  resolved `remoteGoToR` runtime variant of LinkTarget is read-only and never
 *  submitted directly. */
export type EditTarget = LinkTarget | RemoteWireTarget;

// ---------------------------------------------------------------------------
// Inventory (mirror pdfEditor.ts PdfNavInventory)
// ---------------------------------------------------------------------------

export type InventoryDestination =
  | { readonly kind: 'localPage'; readonly pageIndex: number; readonly at?: DestinationPoint }
  | { readonly kind: 'named'; readonly name: string }
  | {
      readonly kind: 'remote';
      readonly path: string;
      /** GoToR `/D` page index (0-based) when the remote destination is a page. */
      readonly page?: number;
      /** GoToR `/D` named destination, when the remote destination is a name. */
      readonly name?: string;
      /** GoToR `/D` `/XYZ` landing point, when one was recorded. */
      readonly at?: DestinationPoint;
    }
  | { readonly kind: 'external'; readonly uri: string }
  | { readonly kind: 'other'; readonly detail: string }
  | { readonly kind: 'none' };

export interface InventoryBookmark {
  readonly handle: string;
  readonly title: string;
  readonly depth: number;
  readonly destination: InventoryDestination;
  readonly destinationResolves: boolean;
}

export interface InventoryLink {
  readonly handle: string;
  readonly page: number;
  readonly rect: Rectangle | null;
  readonly destination: InventoryDestination;
  /** For an AUTHORED remote link, the server's CANONICAL currency verdict of its
   *  frozen saved target (source placement + target identity + pin/package + hash).
   *  Present ⇒ authored: a preview/retarget is allowed only when `current`, else
   *  refused with `detail` (the target changed, or its binding evidence could not
   *  be read). When `current` it carries the AUTHORIZED DESCRIPTOR (targetId,
   *  contextVersion, expectedTargetSha256) from the SAME inventory the check ran
   *  against — the client previews/retargets with exactly these tokens (the
   *  preview/resolve APIs refuse a changed context/hash), so a second client-side
   *  inventory fetch cannot open a stale read-window. Absent ⇒ imported/unbound
   *  (current-resolution semantics). */
  readonly savedTarget?:
    | { readonly current: true; readonly targetId: string; readonly contextVersion: string; readonly expectedTargetSha256: string }
    | { readonly current: false; readonly detail?: string };
}

export interface PdfNavInventory {
  readonly pageCount: number;
  readonly bookmarks: readonly InventoryBookmark[];
  readonly links: readonly InventoryLink[];
  readonly hasOutline: boolean;
  readonly outlineIncomplete: boolean;
  readonly annotationIncomplete: boolean;
  readonly destTableIncomplete: boolean;
}

// ---------------------------------------------------------------------------
// Proposals (mirror generation.ts NavigationProposal)
// ---------------------------------------------------------------------------

export type ProposalOperation = 'createBookmark' | 'createHyperlink' | 'linkExistingToc';

export type EvidenceKind =
  | 'native-heading'
  | 'toc-entry'
  | 'section-reference'
  | 'table-reference'
  | 'figure-reference'
  | 'appendix-reference';

export interface SourceEvidence {
  readonly kind: EvidenceKind;
  readonly page: number;
  readonly text: string;
  readonly rect: Rectangle | null;
  readonly evidenceVersion: number;
}

export interface FrozenTarget {
  readonly kind: 'localPage' | 'named';
  readonly identity: string;
  readonly pageIndex: number | null;
  readonly name: string | null;
  readonly path: string | null;
  readonly anchorHash: string;
  readonly sequence: number;
}

export type ProposalDisposition =
  | { readonly kind: 'auto-accept' }
  | { readonly kind: 'review'; readonly reason: string }
  | { readonly kind: 'unknown'; readonly reason: string }
  | { readonly kind: 'rejected' }
  | { readonly kind: 'manual'; readonly target: LinkTarget }
  | { readonly kind: 'conflict'; readonly reason: string };

export interface NavigationProposal {
  readonly id: string;
  readonly slotKey: string;
  readonly sourceSha256: string;
  readonly operation: ProposalOperation;
  readonly title: string;
  readonly target: LinkTarget;
  readonly frozenTarget: FrozenTarget;
  readonly evidence: SourceEvidence;
  readonly generatorVersion: number;
  readonly evidenceVersion: number;
  readonly candidateCount: number;
  readonly provenance: 'generated';
  readonly disposition: ProposalDisposition;
  readonly detail: string;
}

// ---------------------------------------------------------------------------
// Policy decisions / protection / qualification (mirror policy + draftService)
// ---------------------------------------------------------------------------

export type AuthorizationReason =
  | 'permitted'
  | 'unknown-operation'
  | 'policy-invalid'
  | 'policy-not-activated'
  | 'preserve-mode-forbids-transformation'
  | 'signature-state-denies-mutation'
  | 'operation-disabled-by-policy'
  | 'narrowed-by-user-preference';

export interface AuthorizationResult {
  readonly allowed: boolean;
  readonly reason: AuthorizationReason;
  readonly detail: string;
  readonly operation: NavigationOperation;
}

export type OperationDecisions = Partial<Record<NavigationOperation, AuthorizationResult>>;

export type PdfProtectionState =
  | 'verified-unsigned'
  | 'encrypted'
  | 'signed'
  | 'certified'
  | 'unknown';

export type NavigationSignatureState =
  | 'verified-unsigned'
  | 'signed'
  | 'certified'
  | 'protected'
  | 'unknown';

export interface NavigationProtectionWire {
  readonly state: PdfProtectionState;
  readonly signatureState: NavigationSignatureState;
  readonly mutationAllowedByState: boolean;
  readonly detail: string;
}

export interface NavigationQualificationWire {
  readonly eligible: boolean;
  readonly reason: string;
}

/** A reviewer decision the server could not silently keep: an orphaned slot (its
 *  source no longer appears) or an undecodable stored target. Surfaced so the
 *  reviewer revisits it rather than having the signal swallowed. */
export interface NavigationOverrideSignal {
  readonly slotKey: string;
  readonly reason: string;
}

// ---------------------------------------------------------------------------
// The one response envelope (identical for GET and every mutation)
// ---------------------------------------------------------------------------

export interface NavigationEnvelope {
  readonly ok: true;
  readonly documentId: string;
  readonly submissionId: string;
  readonly sourceSha256: string;
  readonly artifactSha256: string;
  readonly revision: number;
  readonly policyVersion: number;
  readonly inventory: PdfNavInventory | null;
  readonly proposals: readonly NavigationProposal[];
  readonly decisions: OperationDecisions;
  readonly protection: NavigationProtectionWire;
  readonly qualification: NavigationQualificationWire;
  readonly unsupportedReason: string | null;
  readonly draftId: string | null;
  // Additive, optional reviewer signals. `orphanedOverrides` are manual decisions
  // whose source slot no longer appears; `corruptOverrides` are stored manual
  // decisions whose target could not be decoded. Surfaced so they are revisited,
  // never silently lost.
  readonly orphanedOverrides?: readonly NavigationOverrideSignal[];
  readonly corruptOverrides?: readonly NavigationOverrideSignal[];
  /** Whether a byte-changing mutation is currently authorised by a captured,
   *  activated, current policy snapshot for this submission. When false, the
   *  reported decisions show byte-changing operations denied even though the org
   *  policy might permit them — a mutation is refused at commit until the snapshot
   *  is (re)captured. */
  readonly snapshotAuthorizesMutation?: boolean;
}

// ---------------------------------------------------------------------------
// Edit operations (mirror pdfEditor.ts NavEdit)
// ---------------------------------------------------------------------------

export type BookmarkEdit =
  | {
      readonly op: 'createBookmark';
      readonly handle: string;
      readonly parentHandle: string | null;
      readonly index: number | 'append';
      readonly title: string;
      readonly target: EditTarget;
    }
  | { readonly op: 'renameBookmark'; readonly handle: string; readonly title: string }
  | { readonly op: 'removeBookmark'; readonly handle: string }
  | { readonly op: 'reorderBookmark'; readonly handle: string; readonly index: number }
  | { readonly op: 'indentBookmark'; readonly handle: string }
  | { readonly op: 'outdentBookmark'; readonly handle: string }
  | { readonly op: 'retargetBookmark'; readonly handle: string; readonly target: EditTarget };

export type LinkEdit =
  | {
      readonly op: 'createHyperlink';
      readonly handle: string;
      readonly page: number;
      readonly rect: Rectangle;
      readonly target: EditTarget;
      readonly style?: LinkStyle;
    }
  | { readonly op: 'removeHyperlink'; readonly handle: string }
  | { readonly op: 'retargetHyperlink'; readonly handle: string; readonly target: EditTarget }
  | { readonly op: 'styleHyperlink'; readonly handle: string; readonly style: LinkStyle }
  | { readonly op: 'moveHyperlink'; readonly handle: string; readonly rect: Rectangle };

export type NavEdit = BookmarkEdit | LinkEdit;

// ---------------------------------------------------------------------------
// Mutation request bodies
// ---------------------------------------------------------------------------

/** The CAS tokens every mutation echoes, plus the mandatory reason. */
export interface ExpectedTokens {
  readonly expectedRevision: number;
  readonly expectedArtifactSha256: string;
  readonly expectedPolicyVersion: number;
  readonly reason: string;
}

export type EditBody = ExpectedTokens & { readonly edits: readonly NavEdit[] };

export type DispositionBody = ExpectedTokens & {
  readonly decision: 'accept' | 'reject';
  readonly manualTarget?: EditTarget;
};

// ---------------------------------------------------------------------------
// Client
// ---------------------------------------------------------------------------

function base(documentId: string): string {
  return `/api/v1/documents/${encodeURIComponent(documentId)}/navigation`;
}

export const documentNavigation = {
  /** Read-only inventory envelope. Creates no records; works without activation. */
  get: (documentId: string): Promise<NavigationEnvelope> => apiFetch(base(documentId)),

  /** The authed EXACT bytes of a revision (0 = authoritative source). Raw response. */
  file: (documentId: string, revision: number): Promise<Response> =>
    apiFetch(`${base(documentId)}/file?revision=${encodeURIComponent(String(revision))}`, {
      raw: true,
    }),

  generate: (documentId: string, body: ExpectedTokens): Promise<NavigationEnvelope> =>
    apiFetch(`${base(documentId)}/generate`, { method: 'POST', json: body }),

  edit: (documentId: string, body: EditBody): Promise<NavigationEnvelope> =>
    apiFetch(`${base(documentId)}/edit`, { method: 'POST', json: body }),

  dispose: (
    documentId: string,
    proposalId: string,
    body: DispositionBody,
  ): Promise<NavigationEnvelope> =>
    apiFetch(`${base(documentId)}/proposals/${encodeURIComponent(proposalId)}/disposition`, {
      method: 'POST',
      json: body,
    }),

  revert: (documentId: string, body: ExpectedTokens): Promise<NavigationEnvelope> =>
    apiFetch(`${base(documentId)}/revert`, { method: 'POST', json: body }),
};

// ---------------------------------------------------------------------------
// Per-submission navigation CAPABILITY (Kimi D2)
// ---------------------------------------------------------------------------
// The authoritative, server-computed map of documentId → "the navigation editor
// can inspect this (PDF) artifact", derived from the canonical packaging rules.
// The Bookmarks & Links first-applicable auto-select + Previous/Next filter over
// it; the per-document envelope's `unsupportedReason` stays the final authority.

export interface NavigationCapabilityResponse {
  readonly ok: true;
  readonly documents: Readonly<Record<string, boolean>>;
}

export const submissionNavigationCapability = {
  get: (submissionId: string): Promise<NavigationCapabilityResponse> =>
    apiFetch(`/api/v1/submissions/${encodeURIComponent(submissionId)}/navigation-capability`),
};
